/**
 * Curated presets for the Occasions wizard.
 *
 * Each preset proposes a starting set of criteria (food type, tastes, mood)
 * and rules of thumb for quantity per guest. The user can override every
 * field afterwards — these are jumping-off points, not constraints.
 */
import type { IconName } from '@/design-system';

export type FoodCategory =
  | 'red-meat'
  | 'white-meat'
  | 'fish'
  | 'shellfish'
  | 'pasta'
  | 'cheese'
  | 'dessert'
  | 'aperitif'
  | 'asian'
  | 'vegetarian'
  | 'bbq'
  | 'game';

export type Taste =
  | 'full-bodied'
  | 'medium-bodied'
  | 'light'
  | 'fruity'
  | 'oaky'
  | 'mineral'
  | 'spicy'
  | 'tannic'
  | 'crisp'
  | 'sweet'
  | 'sparkling'
  | 'aromatic'
  | 'earthy';

export type WineType = 'red' | 'white' | 'rose' | 'sparkling' | 'dessert' | 'fortified';

export interface OccasionCriteria {
  /** What to serve / pair against */
  foodCategories: FoodCategory[];
  /** Taste profile */
  tastes: Taste[];
  /** Wine types */
  wineTypes: WineType[];
  /** Estimated bottles per guest (e.g. 0.5 = a bottle per two people) */
  bottlesPerGuest: number;
  /** Free-text food notes (sauces, herbs, sides) */
  notes?: string;
  /** Number of guests including host (defaults to 4 in UI) */
  guestCount?: number;
  /** Maximum price per bottle in NOK, optional */
  budgetPerBottleNok?: number;
}

export interface OccasionPreset {
  id: string;
  name: string;
  description: string;
  icon: IconName;
  mood: string;
  criteria: OccasionCriteria;
}

export const OCCASION_PRESETS: OccasionPreset[] = [
  {
    id: 'christmas',
    name: 'Julemiddag',
    description: 'Pinnekjøtt, ribbe eller lutefisk — kraftig mat trenger struktur og syre.',
    icon: 'snow',
    mood: 'christmas',
    criteria: {
      foodCategories: ['red-meat', 'fish'],
      tastes: ['full-bodied', 'tannic', 'oaky'],
      wineTypes: ['red', 'sparkling'],
      bottlesPerGuest: 0.6,
      guestCount: 6,
      notes: 'Tradisjonell norsk julemiddag.',
    },
  },
  {
    id: 'bbq',
    name: 'Grillkveld',
    description: 'Ytrefilet, lammespyd, marinerte grønnsaker — saftig og krydret.',
    icon: 'flame',
    mood: 'bbq',
    criteria: {
      foodCategories: ['red-meat', 'bbq'],
      tastes: ['full-bodied', 'fruity', 'spicy'],
      wineTypes: ['red'],
      bottlesPerGuest: 0.5,
      guestCount: 4,
    },
  },
  {
    id: 'tuesday-red',
    name: 'Tirsdag-rødt',
    description: 'Hverdagsrødt — pasta, gryteretter, pizza. Ikke for tungt.',
    icon: 'moon',
    mood: 'tuesday',
    criteria: {
      foodCategories: ['pasta', 'red-meat'],
      tastes: ['medium-bodied', 'fruity'],
      wineTypes: ['red'],
      bottlesPerGuest: 0.5,
      guestCount: 2,
      budgetPerBottleNok: 200,
    },
  },
  {
    id: 'fish-dinner',
    name: 'Fiske-middag',
    description: 'Kveite, torsk, laks — trenger friskhet og syre, ikke tunge eikepreg.',
    icon: 'fish',
    mood: 'fish',
    criteria: {
      foodCategories: ['fish', 'shellfish'],
      tastes: ['crisp', 'mineral', 'aromatic'],
      wineTypes: ['white', 'sparkling'],
      bottlesPerGuest: 0.5,
      guestCount: 4,
    },
  },
  {
    id: 'anniversary',
    name: 'Bryllupsdag',
    description: 'Spesielt — løsne en bedre flaske, kanskje en boble til aperitiff.',
    icon: 'sparkles',
    mood: 'anniversary',
    criteria: {
      foodCategories: ['red-meat', 'aperitif'],
      tastes: ['full-bodied', 'oaky', 'sparkling'],
      wineTypes: ['red', 'sparkling'],
      bottlesPerGuest: 0.75,
      guestCount: 2,
    },
  },
  {
    id: 'party',
    name: 'Fest',
    description: 'Mange gjester, lett servering, bobler og noe enkel rødt.',
    icon: 'party',
    mood: 'party',
    criteria: {
      foodCategories: ['aperitif', 'cheese', 'asian'],
      tastes: ['sparkling', 'fruity', 'crisp'],
      wineTypes: ['sparkling', 'white', 'rose'],
      bottlesPerGuest: 0.7,
      guestCount: 12,
      budgetPerBottleNok: 250,
    },
  },
  {
    id: 'game-dinner',
    name: 'Viltmiddag',
    description: 'Reinsdyr, elg, hjort, rype — kraftig, jordfarget, krever struktur.',
    icon: 'beef',
    mood: 'game',
    criteria: {
      foodCategories: ['game', 'red-meat'],
      tastes: ['full-bodied', 'earthy', 'tannic'],
      wineTypes: ['red'],
      bottlesPerGuest: 0.6,
      guestCount: 6,
    },
  },
  {
    id: 'cheese-board',
    name: 'Osteanretning',
    description: 'Brie, blåmuggost, kraftige hardoster — bredde i smak og struktur.',
    icon: 'cookie',
    mood: 'cheese',
    criteria: {
      foodCategories: ['cheese'],
      tastes: ['sweet', 'aromatic', 'full-bodied'],
      wineTypes: ['dessert', 'fortified', 'red'],
      bottlesPerGuest: 0.4,
      guestCount: 4,
    },
  },
  {
    id: 'asian-dinner',
    name: 'Asiatisk middag',
    description: 'Sushi, thai, koreansk — søtt, krydret, mye umami. Vil ha aromatisk hvitt.',
    icon: 'cherry',
    mood: 'asian',
    criteria: {
      foodCategories: ['asian', 'fish'],
      tastes: ['aromatic', 'crisp', 'sweet'],
      wineTypes: ['white', 'sparkling'],
      bottlesPerGuest: 0.5,
      guestCount: 4,
    },
  },
];

export const FOOD_CATEGORY_LABELS: Record<FoodCategory, { label: string; icon: IconName }> = {
  'red-meat': { label: 'Rødt kjøtt', icon: 'beef' },
  'white-meat': { label: 'Hvitt kjøtt', icon: 'chef-hat' },
  fish: { label: 'Fisk', icon: 'fish' },
  shellfish: { label: 'Skalldyr', icon: 'fish' },
  pasta: { label: 'Pasta', icon: 'utensils' },
  cheese: { label: 'Ost', icon: 'cookie' },
  dessert: { label: 'Dessert', icon: 'cherry' },
  aperitif: { label: 'Aperitiff', icon: 'sparkles' },
  asian: { label: 'Asiatisk', icon: 'cherry' },
  vegetarian: { label: 'Vegetar', icon: 'grape' },
  bbq: { label: 'Grillet', icon: 'flame' },
  game: { label: 'Vilt', icon: 'beef' },
};

export const TASTE_LABELS: Record<Taste, string> = {
  'full-bodied': 'Fyldig',
  'medium-bodied': 'Middels fylde',
  light: 'Lett',
  fruity: 'Fruktig',
  oaky: 'Eikepreget',
  mineral: 'Mineralsk',
  spicy: 'Krydret',
  tannic: 'Tannin-rik',
  crisp: 'Frisk',
  sweet: 'Søt',
  sparkling: 'Musserende',
  aromatic: 'Aromatisk',
  earthy: 'Jordpreget',
};

export const WINE_TYPE_LABELS: Record<WineType, string> = {
  red: 'Rødvin',
  white: 'Hvitvin',
  rose: 'Rosé',
  sparkling: 'Musserende',
  dessert: 'Dessertvin',
  fortified: 'Sterkvin',
};

/** Map a catalog product into a coarse wine-type bucket using its mainCategory string. */
export function classifyWineType(mainCategory?: string): WineType | null {
  if (!mainCategory) return null;
  const m = mainCategory.toLowerCase();
  if (m.includes('rødvin')) return 'red';
  if (m.includes('hvitvin')) return 'white';
  if (m.includes('rosé') || m.includes('rose')) return 'rose';
  if (m.includes('musser')) return 'sparkling';
  if (m.includes('dessert') || m.includes('søt')) return 'dessert';
  if (m.includes('sterkvin') || m.includes('fortified') || m.includes('madeira') || m.includes('sherry') || m.includes('port')) return 'fortified';
  return null;
}

/** A coarse keyword query string we can hand to /catalog/products/search to surface options. */
export function shoppingQueryFor(criteria: OccasionCriteria): string {
  // Start from the first selected wine type, fall back to first taste, fall back to "rødvin".
  if (criteria.wineTypes.includes('red')) return 'rødvin';
  if (criteria.wineTypes.includes('white')) return 'hvitvin';
  if (criteria.wineTypes.includes('sparkling')) return 'champagne';
  if (criteria.wineTypes.includes('rose')) return 'rosé';
  if (criteria.wineTypes.includes('dessert')) return 'dessert';
  if (criteria.wineTypes[0]) return WINE_TYPE_LABELS[criteria.wineTypes[0]].toLowerCase();
  return 'rødvin';
}
